import { useCallback, useEffect, useRef } from 'react';
import { Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';

// ** MUI Components
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';

// ** Components
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import Spinner from '@erikdakoda/ui/components/Spinner';
import LabelItemDragPreview from './LabelItemDragPreview';
import LabelRemoveZone from './LabelRemoveZone';
import LabelSection from './LabelSection';
import { LabelDropTargetTracker } from './LabelDragTrackers';

// ** Hooks
import {
  useLabelCopyModifier,
  useLabelDragPointer,
  useLabelDragStore,
} from '../hooks/useLabelDragStore';
import { LabelItemMenuContext } from '../hooks/useLabelItemMenu';

// ** Utils
import { normalizedIncludes } from '@erikdakoda/utils/string';
import {
  SECTION_ORDER_DND_TYPE,
  parseItemDraggableId,
  parseSectionDraggableId,
  removeZoneDroppableId,
  sectionDraggableId,
  sectionDroppableId,
  sectionDroppableRegistryKey,
  sectionOrderDroppableId,
} from '../shared/labelDragIds';
import { routeLabelDragEnd } from '../shared/routeLabelDragEnd';

// ** Types
import type { LabelSidebarCallbacks, LabelSidebarProps, LabelSidebarSection } from '../types';
import type { CSSProperties } from 'react';
import type { DraggableStateSnapshot, DraggableStyle } from '@hello-pangea/dnd';

const noMutation = async (): Promise<void> => undefined;

/**
 * When dropping into the empty remove zone, dnd would animate the clone to the zone's top-left.
 * Collapse the clone in place instead so the Motion `Poof` is the only visible feedback.
 */
function removeDropCloneStyle(
  style: DraggableStyle | undefined,
  snapshot: DraggableStateSnapshot,
  removeDroppableId: string,
): CSSProperties | undefined {
  if (
    !style ||
    !snapshot.isDropAnimating ||
    snapshot.draggingOver !== removeDroppableId ||
    !snapshot.dropAnimation
  ) {
    return style as CSSProperties | undefined;
  }
  return {
    ...(style as CSSProperties),
    opacity: 0,
    transitionDuration: '0.001s',
  };
}

/**
 * Data-agnostic sidebar for label-style lists: ordered sections of draggable items, one persisted
 * collapse store per `listId`, and drag callbacks the consumer supplies.
 *
 * Auto sections are not droppable. A loaded auto section with no items hides itself — the `Label`
 * row survives, so the section reappears in place once it has content again. Unloaded auto
 * sections stay visible so the user can open them to load.
 */
export default function LabelSidebar({
  listId,
  droppableType,
  dragScope,
  sections,
  isLoading = false,
  filter,
  ItemComponent,
  renderItem,
  dragDisabled = false,
  disableRemoveZone = false,
  copyDisabled = false,
  emptyText,
  disableSectionActions,
  onRenameSection,
  onTrashSection,
  onMoveSection,
  onMoveItem,
  onCopyItem,
  onRemoveItem,
  onEditSection,
  setDragEndHandler,
  refetch,
}: LabelSidebarProps) {
  'use memo';

  useLabelCopyModifier();
  useLabelDragPointer();
  const copyModifier = useLabelDragStore((state) => state.copyModifier);
  const pendingMutation = useLabelDragStore((state) => state.pendingMutation);
  const dndScope = dragScope ?? droppableType;
  const removeDroppableId = removeZoneDroppableId(dndScope);
  const showPendingOverlay = pendingMutation !== null && pendingMutation !== 'remove';

  const filteredSections: LabelSidebarSection[] =
    filter === ''
      ? sections.map((section) => ({ ...section }))
      : sections.map((section) => ({
          ...section,
          items: section.items.filter((item) => normalizedIncludes(item.name, filter)),
        }));

  const visibleSections = filteredSections.filter(
    (section) =>
      section.kind !== 'auto' || section.items.length > 0 || section.itemsLoaded === false,
  );

  /**
   * Remount the section-order Droppable when nested item Droppables are added/removed so
   * `@hello-pangea/dnd` registers them (new labels are otherwise undroppable until refresh).
   */
  const nestedDroppableRegistryKey = sectionDroppableRegistryKey(
    visibleSections.map((section) => section.id),
  );
  /** Shell spinner only when labels are still loading and nothing is on screen yet. */
  const showShellSpinner = isLoading && visibleSections.length === 0;

  const dragEndState = useRef({ filteredSections, visibleSections, copyModifier });
  dragEndState.current = { filteredSections, visibleSections, copyModifier };

  const resolvedCallbacks: LabelSidebarCallbacks = {
    onMoveSection: onMoveSection ?? noMutation,
    onMoveItem: onMoveItem ?? noMutation,
    onCopyItem: onCopyItem ?? noMutation,
    onRemoveItem: onRemoveItem ?? noMutation,
  };

  const callbacks = useRef(resolvedCallbacks);
  callbacks.current = resolvedCallbacks;

  // keep: stable identity for the setDragEndHandler registration below
  const handleDragEnd = useCallback(
    async (result: DropResult): Promise<boolean> => {
      const state = dragEndState.current;
      return routeLabelDragEnd(result, {
        droppableType,
        dragScope: dndScope,
        sections: state.filteredSections,
        visibleSections: state.visibleSections,
        copyModifierActive: state.copyModifier,
        copyDisabled,
        ...callbacks.current,
      });
    },
    [droppableType, dndScope, copyDisabled],
  );

  useEffect(() => {
    if (dragDisabled) return;
    setDragEndHandler?.(() => handleDragEnd);
  }, [dragDisabled, handleDragEnd, setDragEndHandler]);

  const sectionProps = {
    listId,
    droppableType,
    ItemComponent,
    renderItem,
    emptyText,
    disableSectionActions,
    onRenameSection,
    onTrashSection,
    onEditSection,
    refetch,
  };

  const menuContext = {
    sections,
    onMoveItem: resolvedCallbacks.onMoveItem,
    onCopyItem: resolvedCallbacks.onCopyItem,
    onRemoveItem: resolvedCallbacks.onRemoveItem,
  };

  if (dragDisabled) {
    return (
      <Box className="label-sidebar" sx={{ position: 'relative' }}>
        <LabelItemMenuContext.Provider value={menuContext}>
          {showShellSpinner ? <Spinner fullHeight /> : null}
          {showPendingOverlay ? (
            <Spinner
              size={28}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                padding: 0,
                pointerEvents: 'none',
              }}
            />
          ) : null}
          <List disablePadding sx={{ mb: '36px' }}>
            {visibleSections.map((section) => (
              <LabelSection key={section.id} section={section} dragDisabled {...sectionProps} />
            ))}
          </List>
        </LabelItemMenuContext.Provider>
      </Box>
    );
  }

  return (
    <Box className="label-sidebar" sx={{ position: 'relative' }}>
      <LabelItemMenuContext.Provider value={menuContext}>
        {showShellSpinner ? <Spinner fullHeight /> : null}
        {showPendingOverlay ? (
          <Spinner
            size={28}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              padding: 0,
              pointerEvents: 'none',
            }}
          />
        ) : null}
        <Droppable
          key={nestedDroppableRegistryKey}
          droppableId={sectionOrderDroppableId(dndScope)}
          type={SECTION_ORDER_DND_TYPE}
          renderClone={(provided) => {
            const draggedId = parseSectionDraggableId(
              provided.draggableProps['data-rfd-draggable-id'],
            );
            const draggedSection = visibleSections.find((section) => section.id === draggedId);
            if (!draggedSection) return null;
            return (
              <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
              >
                <Paper elevation={4}>
                  <ListItemButton dense>
                    <ListItemIcon sx={{ minWidth: 'auto', marginRight: 3 }}>
                      <FaIconUvilo icon="chevron-right" />
                    </ListItemIcon>
                    <ListItemText>
                      <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center' }}>
                        <FaIconUvilo icon={draggedSection.icon} sx={{ fontSize: '0.9em' }} />
                        {draggedSection.name}
                      </Box>
                    </ListItemText>
                  </ListItemButton>
                </Paper>
              </div>
            );
          }}
        >
          {(outerProvided) => (
            <List
              disablePadding
              sx={{ marginBottom: 24 }}
              ref={outerProvided.innerRef}
              {...outerProvided.droppableProps}
            >
              {visibleSections.map((section, index) => (
                <Draggable
                  key={section.id}
                  draggableId={sectionDraggableId(section.id)}
                  index={index}
                  isDragDisabled={section.sectionDraggable === false}
                >
                  {(sectionProvided) => (
                    <div ref={sectionProvided.innerRef} {...sectionProvided.draggableProps}>
                      <Droppable
                        droppableId={sectionDroppableId(section.id)}
                        type={droppableType}
                        isDropDisabled={!section.droppable}
                        renderClone={(provided, snapshot) => {
                          const itemId = parseItemDraggableId(
                            droppableType,
                            provided.draggableProps['data-rfd-draggable-id'],
                          );
                          const draggedItem = section.items.find((item) => item.id === itemId);
                          if (!draggedItem) return null;
                          return (
                            <div
                              id={provided.draggableProps['data-rfd-draggable-id']}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                              style={removeDropCloneStyle(
                                provided.draggableProps.style,
                                snapshot,
                                removeDroppableId,
                              )}
                            >
                              <LabelItemDragPreview
                                item={draggedItem}
                                section={section}
                                ItemComponent={ItemComponent}
                                renderItem={renderItem}
                              />
                            </div>
                          );
                        }}
                      >
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            <LabelSection
                              section={section}
                              dragHandleProps={sectionProvided.dragHandleProps}
                              {...sectionProps}
                            />
                            {provided.placeholder}
                            <LabelDropTargetTracker
                              kind={section.kind}
                              isOver={snapshot.isDraggingOver}
                            />
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </Draggable>
              ))}
              {outerProvided.placeholder}
            </List>
          )}
        </Droppable>
        {disableRemoveZone ? null : (
          <LabelRemoveZone droppableType={droppableType} dragScope={dndScope} />
        )}
      </LabelItemMenuContext.Provider>
    </Box>
  );
}
